(() => {
  const CACHE_PREFIX = 'td-reviews-yotpo:';
  const PER_PAGE = 3;

  const STAR_PATH =
    'M6 0 7.347 3.763 11.412 4.145 8.206 6.737 9.193 10.708 6 8.635 2.807 10.708 3.794 6.737.588 4.145 4.653 3.763 6 0Z';

  const formatScore = (value) => (Math.round(Number(value) * 10) / 10).toFixed(1);

  const formatCount = (total) => `${Number(total).toLocaleString('en-US')}+ reviews`;

  const escapeHtml = (value) =>
    String(value ?? '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');

  const getPercentages = (distribution) => {
    const total = [1, 2, 3, 4, 5].reduce((sum, star) => sum + (distribution[String(star)] || 0), 0) || 1;
    const percentages = {};

    for (let star = 1; star <= 5; star += 1) {
      percentages[star] = Math.round(((distribution[String(star)] || 0) / total) * 1000) / 10;
    }

    return percentages;
  };

  const readCache = (cacheKey) => {
    try {
      const raw = localStorage.getItem(`${CACHE_PREFIX}${cacheKey}`);
      if (!raw) return null;

      const parsed = JSON.parse(raw);
      if (!parsed?.fetchedAt || !parsed?.distribution) return null;

      return parsed;
    } catch {
      return null;
    }
  };

  const writeCache = (cacheKey, payload) => {
    try {
      localStorage.setItem(`${CACHE_PREFIX}${cacheKey}`, JSON.stringify(payload));
    } catch {
      /* ignore quota errors */
    }
  };

  const resolveProductId = async (productId, productHandle) => {
    if (productId) return productId;
    if (!productHandle) return null;

    const response = await fetch(`${window.Shopify?.routes?.root || '/'}products/${productHandle}.json`);
    if (!response.ok) return null;

    const data = await response.json();
    return data?.product?.id ? String(data.product.id) : null;
  };

  const fetchYotpoPage = async (appKey, productId, page) => {
    const url = `https://api-cdn.yotpo.com/v1/widget/${encodeURIComponent(appKey)}/products/${encodeURIComponent(
      productId
    )}/reviews.json?per_page=${PER_PAGE}&page=${page}`;
    const response = await fetch(url);
    if (!response.ok) throw new Error(`Yotpo request failed (${response.status})`);

    const data = await response.json();
    const bottomline = data?.response?.bottomline;
    const reviews = Array.isArray(data?.response?.reviews) ? data.response.reviews : [];
    const pagination = data?.response?.pagination || {};
    const total = Number(pagination.total ?? bottomline?.total_review ?? bottomline?.total_reviews ?? 0);

    return {
      bottomline,
      reviews,
      total,
      page: Number(pagination.page || page),
    };
  };

  const applyBottomline = (root, data) => {
    const scoreEl = root.querySelector('[data-td-reviews-score]');
    const countEl = root.querySelector('[data-td-reviews-count]');
    const barsEl = root.querySelector('[data-td-reviews-bars]');

    if (scoreEl) scoreEl.textContent = formatScore(data.averageScore);
    if (countEl) countEl.textContent = formatCount(data.totalReviews);

    root.querySelectorAll('[data-td-reviews-bar]').forEach((bar) => {
      const star = Number(bar.dataset.tdReviewsBar);
      bar.style.width = `${data.percentages[star] ?? 0}%`;
    });

    if (barsEl) {
      barsEl.setAttribute(
        'aria-label',
        `${formatScore(data.averageScore)} out of 5 stars based on ${data.totalReviews} reviews`
      );
    }
  };

  const starsMarkup = (score) => {
    const rating = Number(score) || 0;
    const tenths = Math.round(rating * 10);
    let html = '';

    for (let starIndex = 1; starIndex <= 5; starIndex += 1) {
      const fullThreshold = starIndex * 10 - 4;
      const halfThreshold = starIndex * 10 - 9;
      let fill = 'empty';

      if (tenths >= fullThreshold) fill = 'full';
      else if (tenths >= halfThreshold) fill = 'half';

      if (fill === 'half') {
        html += `<span class="td-reviews__star td-reviews__star--half" aria-hidden="true">
          <svg class="td-reviews__star-bg" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 12 11" fill="currentColor" aria-hidden="true"><path d="${STAR_PATH}"/></svg>
          <svg class="td-reviews__star-fill" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 12 11" fill="currentColor" aria-hidden="true"><path d="${STAR_PATH}"/></svg>
        </span>`;
      } else {
        html += `<span class="td-reviews__star td-reviews__star--${fill}" aria-hidden="true">
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 12 11" fill="currentColor" aria-hidden="true"><path d="${STAR_PATH}"/></svg>
        </span>`;
      }
    }

    return html;
  };

  const reviewCardMarkup = (review) => {
    const name = review?.user?.display_name || review?.user_name || 'Verified buyer';
    const score = review?.score ?? 0;
    const content = review?.content || review?.title || '';
    const image = review?.user_image || review?.user?.social_image || '';
    const scoreLabel = formatScore(score);

    const avatar = image
      ? `<img class="td-reviews__avatar" src="${escapeHtml(image)}" alt="" width="36" height="36" loading="lazy">`
      : `<span class="td-reviews__avatar-placeholder" aria-hidden="true">
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 12 14" fill="none" aria-hidden="true">
            <path d="M6 6.5c1.66 0 3-1.34 3-3S7.66.5 6 .5 3 1.84 3 3.5 4.34 6.5 6 6.5Zm0 1.5c-2.33 0-7 1.17-7 3.5V13h14v-1.5c0-2.33-4.67-3.5-7-3.5Z" fill="currentColor"/>
          </svg>
        </span>`;

    return `<article class="td-reviews__card">
      <div class="td-reviews__card-header">
        ${avatar}
        <div class="td-reviews__meta">
          <p class="td-reviews__name">${escapeHtml(name)}</p>
          <div class="td-reviews__rating-row">
            <div class="td-reviews__card-stars" role="img" aria-label="${escapeHtml(scoreLabel)} out of 5 stars">
              ${starsMarkup(score)}
            </div>
            <p class="td-reviews__rating">${escapeHtml(scoreLabel)}</p>
          </div>
        </div>
      </div>
      ${content ? `<blockquote class="td-reviews__quote">${escapeHtml(content)}</blockquote>` : ''}
    </article>`;
  };

  const updateLoadMore = (root, loadedCount, total) => {
    const button = root.querySelector('[data-td-reviews-load-more]');
    if (!button) return;

    const hasMore = loadedCount < total;
    button.hidden = !hasMore;
    button.disabled = false;
    button.removeAttribute('aria-busy');
  };

  const appendReviews = (root, reviews) => {
    const list = root.querySelector('[data-td-reviews-list]');
    if (!list || !reviews.length) return;

    list.insertAdjacentHTML('beforeend', reviews.map(reviewCardMarkup).join(''));
  };

  class TdReviews extends HTMLElement {
    #page = 0;
    #total = 0;
    #loaded = 0;
    #productId = '';
    #loading = false;

    connectedCallback() {
      this.list = this.querySelector('[data-td-reviews-list]');
      this.loadMore = this.querySelector('[data-td-reviews-load-more]');

      this.loadMore?.addEventListener('click', () => {
        this.loadNextPage();
      });

      this.init();
    }

    async init() {
      const appKey = this.dataset.yotpoAppKey;
      const productId = this.dataset.yotpoProductId || '';
      const productHandle = this.dataset.yotpoProductHandle || '';
      const cacheHours = Number(this.dataset.yotpoCacheHours || 24);
      const cacheKey = this.dataset.yotpoCacheKey || `${appKey}:${productId || productHandle}`;
      const cacheTtlMs = Math.max(1, cacheHours) * 60 * 60 * 1000;

      if (!appKey || (!productId && !productHandle)) return;

      const cached = readCache(cacheKey);
      if (cached && Date.now() - cached.fetchedAt < cacheTtlMs) {
        applyBottomline(this, cached);
      }

      try {
        this.#productId = await resolveProductId(productId, productHandle);
        if (!this.#productId) return;

        await this.loadNextPage({ cacheKey, cacheTtlMs, cached });
      } catch {
        if (cached) applyBottomline(this, cached);
      }
    }

    async loadNextPage({ cacheKey, cacheTtlMs, cached } = {}) {
      if (this.#loading) return;
      if (this.#page > 0 && this.#loaded >= this.#total) return;

      const appKey = this.dataset.yotpoAppKey;
      if (!appKey || !this.#productId) return;

      this.#loading = true;
      if (this.loadMore) {
        this.loadMore.disabled = true;
        this.loadMore.setAttribute('aria-busy', 'true');
      }

      try {
        const nextPage = this.#page + 1;
        const payload = await fetchYotpoPage(appKey, this.#productId, nextPage);
        const bottomline = payload.bottomline;

        if (bottomline?.star_distribution) {
          const stats = {
            fetchedAt: Date.now(),
            averageScore: bottomline.average_score ?? bottomline.averageScore ?? 0,
            totalReviews: bottomline.total_review ?? bottomline.total_reviews ?? 0,
            distribution: bottomline.star_distribution,
            percentages: getPercentages(bottomline.star_distribution),
          };

          if (cacheKey) writeCache(cacheKey, stats);
          applyBottomline(this, stats);
        } else if (cached && Date.now() - cached.fetchedAt < (cacheTtlMs || 0)) {
          applyBottomline(this, cached);
        }

        this.#page = nextPage;
        this.#total = payload.total;
        this.#loaded += payload.reviews.length;
        appendReviews(this, payload.reviews);
        updateLoadMore(this, this.#loaded, this.#total);

        if (this.list && this.#loaded > 0) {
          this.list.hidden = false;
        }
      } catch {
        if (cached) applyBottomline(this, cached);
        updateLoadMore(this, this.#loaded, this.#total);
      } finally {
        this.#loading = false;
        if (this.loadMore && this.#loaded < this.#total) {
          this.loadMore.disabled = false;
          this.loadMore.removeAttribute('aria-busy');
        }
      }
    }
  }

  if (!customElements.get('td-reviews')) {
    customElements.define('td-reviews', TdReviews);
  }
})();
